import { api } from '@/helpers/apiClient'
import { toQueryString } from '@/helpers/queryString'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  treeDtoSchema,
  treeListDtoSchema,
  type TreeDto,
  type TreeListDto,
} from '@lib/dto/tree.dto'
import type {
  CreateTreeInput,
  TreeSyncOperation,
  UpdateTreeInput,
} from '@lib/input/tree.input'
import type { ITreeService } from '@/types/TreeService.types'

/** Service for a site's surveyed trees. */
class TreeService implements ITreeService {
  /**
   * A site's trees, with what one visit found on them: `visitId`, or the
   * site's current tree visit when it is left out or null.
   */
  async listBySite(
    siteId: string,
    includeArchived = false,
    visitId: string | null = null,
  ): Promise<TreeListDto> {
    try {
      const query = toQueryString({
        ...(includeArchived ? { includeArchived: 'true' } : {}),
        ...(visitId === null ? {} : { visitId }),
      })
      return await api.get(`/sites/${siteId}/trees${query}`, treeListDtoSchema)
    } catch (error) {
      logger.error('Failed to list trees', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async add(siteId: string, input: CreateTreeInput): Promise<TreeDto> {
    try {
      return await api.post(`/sites/${siteId}/trees`, input, treeDtoSchema)
    } catch (error) {
      logger.error('Failed to add tree', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(id: string, input: UpdateTreeInput): Promise<TreeDto> {
    try {
      return await api.put(`/trees/${id}`, input, treeDtoSchema)
    } catch (error) {
      logger.error('Failed to update tree', { id, error: toErrorMessage(error) })
      throw error
    }
  }

  async setArchived(id: string, archived: boolean): Promise<TreeDto> {
    try {
      const action = archived ? 'archive' : 'restore'
      return await api.post(`/trees/${id}/${action}`, {}, treeDtoSchema)
    } catch (error) {
      logger.error('Failed to change tree archived state', {
        id,
        archived,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Flush a batch of queued field changes.
   *
   * Returns the site's authoritative live set rather than just what changed, so
   * the caller replaces its local copy instead of trying to reconcile. After a
   * long spell offline the device's idea of the site can be wrong in ways it
   * cannot detect — a tree archived from a desk, say — and asking "what is
   * true now?" is both simpler and more correct than merging.
   */
  async sync(
    siteId: string,
    operations: TreeSyncOperation[],
    /** The visit this phone is working in, whose findings to answer with. */
    visitId: string | null = null,
  ): Promise<TreeListDto> {
    try {
      return await api.post(
        `/sites/${siteId}/trees/sync`,
        { operations, visitId },
        treeListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to sync trees', {
        siteId,
        operations: operations.length,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const treeService: ITreeService = new TreeService()
